import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { AlertTriangle, Check, Copy, Download, Search, UserPlus, Users } from 'lucide-react';
import { db } from '../config/firebase';
import { classNames } from '../lib/utils';
import { exportEmployeesToExcel } from '../lib/exports';
import { PERMISSION_ROLES, buildKrCodeIndex, resolveAuthCleanup } from '../lib/employeeAdmin';
import { AddEmployeeModal, DeleteEmployeeModal, EmployeeRow } from '../features/employees';

const ROLE_FILTERS = ['All', ...PERMISSION_ROLES, 'Other'];

/**
 * Admin-only roster management: everyone in the database, what FY26-27 KR access
 * they hold, and the ability to add or remove a person.
 *
 * Reads `users` and `teams` straight from the props App already keeps live, so
 * the list refreshes itself after any change with no refetch here. Errors stay
 * local to this view — routing them through App's permissionError would replace
 * the whole app with the full-screen rules page over one failed row.
 */
export default function EmployeeManagementView({
  users, teams, okrsFY2627, viewer, onCreateEmployee, onDeleteEmployee, onUpdateManager,
}) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [expandedId, setExpandedId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cleanupQueue, setCleanupQueue] = useState([]);
  const [copied, setCopied] = useState(false);
  const [exportError, setExportError] = useState('');

  const viewerUid = viewer.uid || viewer.id;
  const krCodeIndex = useMemo(() => buildKrCodeIndex(okrsFY2627), [okrsFY2627]);
  const adminCount = useMemo(() => (users || []).filter(u => u.role === 'Admin').length, [users]);

  /** Logins the browser could not delete, still waiting on scripts/deleteUsers.js. */
  useEffect(() => {
    const pending = query(collection(db, 'authCleanupQueue'), where('resolved', '==', false));
    const unsubscribe = onSnapshot(
      pending,
      (snapshot) => setCleanupQueue(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
      // Non-fatal: the queue is a convenience, not part of the roster.
      (error) => console.error('Could not read the auth cleanup queue:', error)
    );
    return () => unsubscribe();
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || [])
      .filter(u => {
        if (roleFilter === 'Other' && PERMISSION_ROLES.includes(u.role)) return false;
        if (roleFilter !== 'All' && roleFilter !== 'Other' && u.role !== roleFilter) return false;
        if (!q) return true;
        return [u.name, u.email, u.role, teams[u.teamId]?.name, u.team]
          .some(v => (v || '').toLowerCase().includes(q));
      })
      .slice()
      .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
  }, [users, teams, search, roleFilter]);

  /** Export whoever is currently listed, so a search or role filter narrows the file too. */
  const handleExport = () => {
    setExportError('');
    try {
      exportEmployeesToExcel(visible, teams, krCodeIndex);
    } catch (error) {
      console.error('Employee export failed:', error);
      setExportError('Could not build the export file.');
    }
  };

  const copyCleanupEmails = async () => {
    // Exactly the format scripts/deleteUsersList.txt expects: one address per line.
    const text = cleanupQueue.map(e => e.email).filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      console.error('Clipboard write failed:', error);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Employee management</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {users.length} {users.length === 1 ? 'person' : 'people'} in the database
                {visible.length !== users.length && ` · ${visible.length} shown`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleExport}
                disabled={visible.length === 0}
                title={visible.length === users.length
                  ? 'Download all employees as Excel'
                  : `Download the ${visible.length} listed ${visible.length === 1 ? 'employee' : 'employees'} as Excel`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} /> Export
              </button>
              <button
                type="button"
                onClick={() => setIsAddOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
              >
                <UserPlus size={16} /> Add employee
              </button>
            </div>
          </div>

          {exportError && (
            <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{exportError}</div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, role or team…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-1 focus:ring-blue-500 outline-none"
              aria-label="Filter by role"
            >
              {ROLE_FILTERS.map(r => <option key={r} value={r}>{r === 'All' ? 'All roles' : r}</option>)}
            </select>
          </div>
        </div>

        {cleanupQueue.length > 0 && (
          <div className="px-4 sm:px-6 py-3 bg-amber-50 border-b border-amber-200">
            <div className="flex items-start gap-2">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-900">
                  {cleanupQueue.length} {cleanupQueue.length === 1 ? 'login still needs' : 'logins still need'} removing
                </p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  These profiles are gone, but their Firebase logins can only be deleted with the Admin SDK.
                  Copy the addresses into <span className="font-mono">scripts/deleteUsersList.txt</span>, then run
                  {' '}<span className="font-mono">npm run users:delete:dry</span> followed by
                  {' '}<span className="font-mono">npm run users:delete</span>.
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {cleanupQueue.map(entry => (
                    <span key={entry.id} className="inline-flex items-center gap-1.5 text-[11px] bg-white border border-amber-200 rounded px-2 py-0.5">
                      <span className="font-mono text-amber-900">{entry.email}</span>
                      <button
                        type="button"
                        onClick={() => resolveAuthCleanup(entry.id)}
                        title="Mark as done"
                        className="text-amber-600 hover:text-emerald-700"
                      >
                        <Check size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={copyCleanupEmails}
                  className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-amber-900 bg-white border border-amber-300 rounded px-2 py-1 hover:bg-amber-100"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy addresses'}
                </button>
              </div>
            </div>
          </div>
        )}

        {visible.length === 0 ? (
          <div className="text-center py-16">
            <Users size={36} className="mx-auto text-gray-300" />
            <p className="text-sm text-gray-500 mt-3">
              {search || roleFilter !== 'All' ? 'No one matches that search.' : 'No users in the database.'}
            </p>
          </div>
        ) : (
          <div>
            {visible.map(user => (
              <EmployeeRow
                key={user.id}
                user={user}
                users={users}
                teams={teams}
                krCodeIndex={krCodeIndex}
                isExpanded={expandedId === user.id}
                onToggle={() => setExpandedId(id => (id === user.id ? null : user.id))}
                onDelete={setDeleteTarget}
                onUpdateManager={onUpdateManager}
                isSelf={user.id === viewerUid}
                isLastAdmin={user.role === 'Admin' && adminCount <= 1}
              />
            ))}
          </div>
        )}
      </div>

      <p className={classNames('text-[11px] text-gray-400 mt-2 px-1')}>
        Role changes are read once at sign-in, so anyone whose role you change needs to reload before it takes effect.
      </p>

      <AddEmployeeModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        users={users}
        onCreate={onCreateEmployee}
      />

      <DeleteEmployeeModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        user={deleteTarget}
        users={users}
        onConfirm={onDeleteEmployee}
      />
    </div>
  );
}
